import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTimes, FaChevronLeft, FaChevronRight, FaArrowLeft } from 'react-icons/fa';
import './Gallery.css';

import { API_BASE_URL, IMG_BASE } from "../apiConfig";

const Gallery = () => {
    const { albumId } = useParams();
    const navigate = useNavigate();
    const [albums, setAlbums] = useState([]);
    const [filteredAlbums, setFilteredAlbums] = useState([]);
    const [selectedYear, setSelectedYear] = useState("All");
    const [sortOrder, setSortOrder] = useState("recent");
    const [loading, setLoading] = useState(true);
    const [selectedGallery, setSelectedGallery] = useState(null);
    const [galleryImages, setGalleryImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const getYearOptions = () => {
        const startYear = 2016;
        const currentYear = new Date().getFullYear();
        const endYear = Math.max(2026, currentYear);
        const years = [];
        for (let y = endYear; y >= startYear; y--) {
            years.push(y);
        }
        return years;
    };

    const formatEventDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'long' });
        const year = date.getFullYear();

        const getOrdinal = (n) => {
            const s = ["th", "st", "nd", "rd"];
            const v = n % 100;
            return (s[(v - 20) % 10] || s[v] || s[0]);
        };

        return `${day}${getOrdinal(day)} ${month}, ${year}`;
    };

    useEffect(() => {
        const fetchAlbums = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/gallery`);
                const data = await response.json();
                setAlbums(data);
            } catch (error) {
                console.error("Error fetching gallery list:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAlbums();
    }, []);

    useEffect(() => {
        if (albumId && albums.length > 0) {
            const album = albums.find(a => a.GalleryEventId.toString() === albumId.toString());
            if (album) setSelectedGallery(album);
        }
    }, [albumId, albums]);

    useEffect(() => {
        if (selectedGallery) {
            const fetchImages = async () => {
                setLoadingImages(true);
                try {
                    const response = await fetch(`${API_BASE_URL}/api/gallery/${selectedGallery.GalleryEventId}`);
                    const data = await response.json();
                    setGalleryImages(data);
                } catch (error) {
                    console.error("Error fetching gallery images:", error);
                } finally {
                    setLoadingImages(false);
                }
            };
            fetchImages();
        }
    }, [selectedGallery]);

    useEffect(() => {
        let results = [...albums];

        // 1. Filter by selected year
        if (selectedYear !== "All") {
            results = results.filter(x => {
                if (!x.EventDate) return false;
                const date = new Date(x.EventDate);
                const year = isNaN(date.getTime()) ? null : date.getFullYear();
                return year === parseInt(selectedYear);
            });
        }

        // 2. Sort by event date
        results.sort((a, b) => {
            const dateA = new Date(a.EventDate || 0);
            const dateB = new Date(b.EventDate || 0);
            if (sortOrder === "recent") {
                return dateB - dateA;
            } else {
                return dateA - dateB;
            }
        });

        setFilteredAlbums(results);
    }, [selectedYear, sortOrder, albums]);

    const openLightbox = (index) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);

    const showPrev = (e) => {
        e.stopPropagation();
        setLightboxIndex(prev => (prev === 0 ? galleryImages.length - 1 : prev - 1));
    };

    const showNext = (e) => {
        e.stopPropagation();
        setLightboxIndex(prev => (prev === galleryImages.length - 1 ? 0 : prev + 1));
    };

    if (selectedGallery) {
        return (
            <div className="gallery-page-wrapper">
                <section className="gallery-hero-section" style={{
                    backgroundImage: `url(${selectedGallery.CoverImage ? (selectedGallery.CoverImage.startsWith('http') ? selectedGallery.CoverImage : IMG_BASE + selectedGallery.CoverImage) : ""})`,
                    backgroundSize: "cover", backgroundPosition: "center"
                }}>
                    <div className="gallery-hero-overlay"></div>
                    <div className="gallery-hero-content fade-up">
                        <h1 className="gallery-hero-title">{selectedGallery.Title}</h1>
                        <p className="gallery-hero-date" style={{ color: 'white', fontSize: '1.2rem', marginTop: '10px' }}>
                            {formatEventDate(selectedGallery.EventDate)}
                        </p>
                        {selectedGallery.Description && (
                            <p className="gallery-hero-description" style={{ color: 'rgba(255,255,255,0.9)', maxWidth: '800px', margin: '15px auto 0 auto', lineHeight: '1.6' }}>
                                {selectedGallery.Description}
                            </p>
                        )}
                    </div>
                </section>

                <section className="gallery-albums-section">
                    <div className="gallery-albums-container">
                        <button onClick={() => { setSelectedGallery(null); navigate('/gallery'); }} className="back-btn" style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '8px', background: '#f90202ff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                            <FaArrowLeft /> Back to Albums
                        </button>

                        {loadingImages ? (
                            <p>Loading photos...</p>
                        ) : (
                            <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                                {galleryImages.map((img, index) => (
                                    <div key={img.ImageId} className="gallery-item" onClick={() => openLightbox(index)} style={{ cursor: 'pointer', borderRadius: '12px', overflow: 'hidden', height: '200px' }}>
                                        <img 
                                            src={img.ImageFile.startsWith('http') ? img.ImageFile : IMG_BASE + img.ImageFile} 
                                            alt={`Gallery ${index}`} 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                                            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
                                            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* Lightbox */}
                {lightboxIndex !== null && (
                    <div className="event-lightbox-overlay" onClick={closeLightbox} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="event-lightbox-content" onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
                            <button className="event-lightbox-close" onClick={closeLightbox} style={{ position: 'absolute', top: '-40px', right: 0, background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}><FaTimes /></button>
                            <button className="event-lightbox-nav prev" onClick={showPrev} style={{ position: 'absolute', left: '-60px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'white', fontSize: '36px', cursor: 'pointer' }}><FaChevronLeft /></button>
                            <img src={galleryImages[lightboxIndex].ImageFile.startsWith('http') ? galleryImages[lightboxIndex].ImageFile : IMG_BASE + galleryImages[lightboxIndex].ImageFile} alt="" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }} />
                            <button className="event-lightbox-nav next" onClick={showNext} style={{ position: 'absolute', right: '-60px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'white', fontSize: '36px', cursor: 'pointer' }}><FaChevronRight /></button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="gallery-page-wrapper">
            {/* ================= GALLERY HERO ================= */}
            <section
                className="gallery-hero-section"
                style={{
                    backgroundImage: "url('https://mks.org.au/wp-content/uploads/2026/02/IMG_1675.jpg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat"
                }}
            >
                <div className="gallery-hero-overlay"></div>
                <div className="gallery-hero-content fade-up">
                    <h1 className="gallery-hero-title">Gallery</h1>
                </div>
            </section>

            {/* ================= ALBUMS GRID ================= */}
            <section className="gallery-albums-section">
                <div className="gallery-albums-container">
                    {/* ================= FILTERS PANEL ================= */}
                    <div className="gallery-filters" style={{ display: "flex", gap: "20px", marginBottom: "40px", flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <label style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#333" }}>Filter by Year</label>
                            <select
                                value={selectedYear}
                                onChange={e => setSelectedYear(e.target.value)}
                                style={{
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    backgroundColor: "#fff",
                                    fontSize: "1rem",
                                    color: "#333",
                                    minWidth: "160px",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                                    outline: "none"
                                }}
                            >
                                <option value="All">All Years</option>
                                {getYearOptions().map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <label style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#333" }}>Sort Order</label>
                            <select
                                value={sortOrder}
                                onChange={e => setSortOrder(e.target.value)}
                                style={{
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    backgroundColor: "#fff",
                                    fontSize: "1rem",
                                    color: "#333",
                                    minWidth: "180px",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                                    outline: "none"
                                }}
                            >
                                <option value="recent">Recent to Old</option>
                                <option value="old">Old to Recent</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <p>Loading albums...</p>
                    ) : filteredAlbums.length === 0 ? (
                        <div style={{ textAlign: "center", width: "100%", padding: "40px", color: "#666" }}>
                            <p style={{ fontSize: "1.2rem" }}>No albums found for the selected filter.</p>
                        </div>
                    ) : (
                        <div className="gallery-albums-grid">
                            {filteredAlbums.map((album) => (
                                <div 
                                    key={album.GalleryEventId} 
                                    className="gallery-album-card"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedGallery(album)}
                                >
                                    <div className="gallery-album-img-wrapper">
                                        <img 
                                            src={album.CoverImage ? (album.CoverImage.startsWith('http') ? album.CoverImage : IMG_BASE + album.CoverImage) : ""} 
                                            alt={album.Title} className="gallery-album-img" 
                                        />
                                    </div>
                                    <div className="gallery-album-content">
                                        <h4 className="gallery-album-title"><span>{album.Title}</span></h4>
                                        <div className="gallery-album-details">
                                            <p><strong>Event Date:</strong> {formatEventDate(album.EventDate)}</p>
                                            {album.Description && (
                                                <p className="gallery-album-description" style={{ fontSize: '0.9rem', color: '#666', marginTop: '8px', lineHeight: '1.4' }}>
                                                    {album.Description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Gallery;
